import { Injectable } from '@angular/core';
import { CloudFunctionAuthError, CloudFunctionHttpError, CloudFunctionsService, isNetworkError } from 'services/cloud-functions.service';
import { AuthService } from 'services/auth.service';
import { CouchLoginReason } from 'services/couch-login-storm';
import { UserDb } from 'services/user-db.service';
import { SvPlanHandlerService } from 'services/server/sv-plan-handler.service';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { LoadingController, Loading } from 'providers/loading-controller';
import { getMyApp } from 'app/app-interface';
import { LL } from 'i18n/i18n.service';
import { deepOverwriteCouchSafe } from '@cf-shared/deep-helpers';
import { mapIntakeFormToDeclaredState, mapIntakeFormToImmutableState } from '@cf-shared/data-model/intake-form-helpers';
import { FitnessPlan, WelcomePhase } from '@cf-shared/data-model/fitness-plan';
import { DatabaseWrapper } from 'services/database-wrapper';
import { didPushReachServer } from 'services/database-wrapper-force-push';
import { UrlQueryService } from 'services/url-query-service';
import { AuthRole } from '@cf-shared/couch-auth';
import { LoggingService } from 'services/logging.service';
import { removeFormerCoachByUid } from 'services/coach-claim-helpers';
import { ClaimBridgeReloadDecision, decideClaimBridgeReload, parseClaimBridgeRearmCount } from 'services/coach-claim-reload-bridge';
import { describeError } from '@cf-shared/error-describe';
import { CoachValidateClaimTokenResult } from '@cf-shared/coach-interface';
import {
  classifyCoachClaimFailure,
  classifyCoachTokenCheckFailure,
  CoachClaimFailureKind,
  CoachClaimFailureMessage,
  CoachTokenCheckOutcome,
  decideCoachTokenCheckOutcome,
  pickCoachClaimFailureMessage,
} from 'services/coach-claim-failure-policy';
import { CoachBranding } from '@cf-shared/data-model/coach';
import { DateTime } from 'luxon';
import { TranslatedString } from 'models/translated-string';
import { timeout } from '@cf-shared/shared-util';

export interface CoachClaimProcessOptions {
  checkOverwrite?: boolean;
}

/**
 * What `validateTokenAsync` concluded about the invitation link in the URL.
 *
 * A client-side shape rather than the wire result, because the wire result cannot express the
 * difference that matters here: on the wire, "this token is dead" and "I could not look" are both
 * `valid: false`. The coach details are present only for `Valid`.
 */
export interface CoachTokenCheckResult {
  outcome: CoachTokenCheckOutcome;
  coachName?: string;
  branding?: CoachBranding;
  /** the coach sent a blank intake, so no program exists yet (#6793); absent from an older server */
  clientFills?: boolean;
}

const COACH_CLAIM_PENDING_RELOAD_KEY = 'coachClaimPendingReload';
/** How many times the pending-reload marker above has already been carried across a restart (#4533). */
const COACH_CLAIM_BRIDGE_REARMS_KEY = 'coachClaimBridgeRearms';

/** How long the claim waits for Firebase sign-in to settle before calling the server anyway (#4466).
 *  Matches the ceiling the food search and SSO service already use for the same wait. */
const FIREBASE_AUTH_SETTLE_WAIT_MS = 10_000;

/** How long the claim waits for the user database's documents to finish loading before it
 *  refuses outright (#4801). Matches the Firebase settle wait beside it — both are "give
 *  start-up a fair chance, then stop" — but the two differ in what happens next: this one
 *  refuses, that one proceeds. See `waitForUserDatabaseReadyAsync` for why. */
const USER_DB_READY_WAIT_MS = 10_000;
/** How often that wait re-asks. Short enough that a normal boot adds no visible delay. */
const USER_DB_READY_POLL_MS = 250;

@Injectable({ providedIn: 'root' })
export class CoachClaimService {

  private claimInProgress = false;
  private unstickInProgress = false;

  constructor(
    private cloudFunctions: CloudFunctionsService,
    private auth: AuthService,
    private userDb: UserDb,
    private svPlanHandler: SvPlanHandlerService,
    private alertCtrl: AlertController,
    private loadingCtrl: LoadingController,
    private urlQuery: UrlQueryService,
    private logging: LoggingService,
  ) {}

  /** Whether a claim token is waiting in the URL or a claim was just completed (pre-reload) */
  hasPendingClaimToken(): boolean {
    if (this.urlQuery.getUrlParam('coachInviteToken')) return true;

    // after a successful claim, a sessionStorage flag bridges the reload window
    // where CouchDB roles may not yet have propagated
    if (sessionStorage.getItem(COACH_CLAIM_PENDING_RELOAD_KEY) === '1') {
      // if role has propagated, clear the flag — no longer needed
      if (this.auth.getAuthRole() >= AuthRole.Paid) {
        sessionStorage.removeItem(COACH_CLAIM_PENDING_RELOAD_KEY);
        return false;
      }
      return true;
    }

    return false;
  }

  private getClaimTokenFromUrl(): string | null {
    return this.urlQuery.getUrlParam('coachInviteToken') || null;
  }

  private clearClaimTokenFromUrl() {
    this.urlQuery.clearUrlSearchKeys(['coachInviteToken']);
  }

  private getSlotId(token: string): string {
    try {
      const payload = JSON.parse(atob(token.split('.')[0]));
      return payload.s || '?';
    } catch {
      return '?';
    }
  }

  // shared shape for all trackSilentProblem(...) calls in this service —
  // dedupes the per-call payload so the slotId / err message / stack contract
  // stays consistent across the three diagnostic catch sites (#3315).
  private buildErrContext(slotId: string, err: any) {
    return {
      slotId,
      err: err?.message || String(err),
      stack: err?.stack,
    };
  }

  // shared exit for both failure paths in processClaimAsync — the server returned success:false
  // (an expected, user-visible rejection) and the outer catch (an unexpected throw, already
  // trackSilentProblem'd by the caller). Both must clear the token, dismiss the spinner, show an
  // alert, and reload — keeping those steps in one place avoids drift.
  //
  // #4466: both paths used to show one sentence — "This invitation link is invalid or has already
  // been used." — and that was the bug. Only the server can make that claim about the token; a
  // throw means we never got a verdict on it at all. So the KIND of failure comes in and the shared
  // policy picks the wording. See coach-claim-failure-policy.ts for the full reasoning.
  //
  // Why the token is always cleared, whatever the kind: leaving it in the URL would send the reload
  // straight back to CoachClaimPage, which claims again on ionViewDidEnter, fails again, and
  // reloads again — an endless loop on a new client's very first screen. For the kinds where the
  // server never got as far as a verdict, the token is still unspent and the wording tells them to
  // open their invitation link again; for a failure AFTER the claim was accepted it is spent, and
  // the wording says so instead.
  private async dismissAndShowFailureAsync(loading: Loading, kind: CoachClaimFailureKind): Promise<void> {
    this.clearClaimTokenFromUrl();
    await loading.dismiss();
    await this.alertCtrl.showAlertAsync({
      title: LL.coach.claim.title(),
      message: this.claimFailureMessage(kind),
      buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: true }],
    });
    await getMyApp().reloadAppAsync({ resetToRoot: true });
  }

  /** The words for each thing the policy says we are allowed to claim. */
  private claimFailureMessage(kind: CoachClaimFailureKind): TranslatedString {
    switch (pickCoachClaimFailureMessage(kind)) {
      case CoachClaimFailureMessage.LinkIsBad:
        return LL.coach.claim.invalidToken();
      case CoachClaimFailureMessage.ConnectedButSetupUnfinished:
        return LL.coach.claim.connectedSetupUnfinished();
      case CoachClaimFailureMessage.ReopenTheLink:
        return LL.coach.claim.couldNotComplete();
    }
  }

  /**
   * Check whether the invitation link in the URL is still good, without claiming it.
   *
   * Answers three states rather than a boolean, and that is the whole point of this method. It
   * used to return `{ valid: false }` for a dropped connection, a timeout and a server error
   * alike — the same answer the server gives for a link that really is dead — and the login page
   * turned that into "this invitation link is invalid or has already been used". A brand-new
   * client on a flaky connection was sent away from a working invite on the first screen they
   * ever see, and nobody on our side found out, because the only trace was a console.log on their
   * device. See coach-claim-failure-policy.ts for the reasoning; this is #4466's bug, one call up.
   */
  async validateTokenAsync(): Promise<CoachTokenCheckResult> {
    const token = this.getClaimTokenFromUrl();
    // no token in the URL at all. Nothing was checked, but there is nothing to retry either, so
    // this answers the same non-retryable outcome the old boolean did rather than offering a Try
    // Again that could only fail the same way.
    //
    // Note that `hasPendingClaimToken()` — which is what puts the login page into the claim flow —
    // is ALSO true on the post-claim sessionStorage reload bridge, where no URL token exists. That
    // case does not reach here in practice, but the reason is routing rather than anything this
    // method can see: app.component.ts sends a pending-claim user to CoachClaimPage, and bounces an
    // already-logged-in user off LoginPage entirely.
    if (!token) return { outcome: CoachTokenCheckOutcome.RejectedByServer };
    const slotId = this.getSlotId(token);
    console.log(`[CLAIM] validateToken: slot=${slotId}`);

    let result: CoachValidateClaimTokenResult;
    try {
      result = await this.cloudFunctions.coachValidateClaimTokenAsync({ claimToken: token });
      console.log(`[CLAIM] validateToken: result=${JSON.stringify(result)}`);
    } catch (err) {
      console.log(`[CLAIM] validateToken: error`, err);
      return this.reportTokenCheckCouldNotComplete(slotId, err, { serverAnswered: false });
    }

    const outcome = decideCoachTokenCheckOutcome({
      serverAnswered: true,
      serverSaysValid: result.valid,
      serverCouldNotCheck: !!result.couldNotCheck,
    });
    // the server answered normally and told us its own read failed, so it never reached a verdict
    // either. Worth reporting for the same reason as a throw: nothing else records it.
    if (outcome === CoachTokenCheckOutcome.CouldNotCheck) {
      return this.reportTokenCheckCouldNotComplete(slotId, undefined, { serverAnswered: true });
    }
    if (outcome === CoachTokenCheckOutcome.RejectedByServer) {
      return { outcome };
    }
    return { outcome, coachName: result.coachName, branding: result.branding, clientFills: result.clientFills };
  }

  /**
   * One exit for both ways the check can fail to reach a verdict, so the report cannot drift
   * between them.
   *
   * The kind goes in the report NAME, not only in the context: Sentry context becomes a breadcrumb
   * and is not indexed, so naming the cause only there would group every cause into one issue
   * nobody can split (#4466 had to learn this on the claim call).
   */
  private reportTokenCheckCouldNotComplete(
    slotId: string,
    err: any,
    input: { serverAnswered: boolean },
  ): CoachTokenCheckResult {
    const kind = classifyCoachTokenCheckFailure({
      serverAnswered: input.serverAnswered,
      // isNetworkError covers the connection failing; it deliberately does NOT match an HTTP
      // status, and 5xx is only retried for 502/503 — so a plain 500 needs naming separately or
      // it would fall through as unexplained
      isServerUnreachable: isNetworkError(err),
      isServerError: err instanceof CloudFunctionHttpError && err.httpStatus >= 500,
    });
    this.logging.trackSilentProblem(`CoachClaim: could not check invitation token (${kind})`, {
      ...this.buildErrContext(slotId, err),
      kind,
      // what this cost the person, which the error alone does not say: a new client was stopped
      // on the first screen of the app by a link that is probably fine.
      consequence: 'client shown the could-not-check screen on the login page',
    });
    return { outcome: CoachTokenCheckOutcome.CouldNotCheck };
  }

  /**
   * Put the pending-reload marker back before restarting, so a stale answer after the
   * restart lands this person on the Coach Claim page rather than on the paywall (#4533).
   *
   * One "paid" answer is not proof the NEXT login will say the same: CouchDB serves the
   * sign-in through a cache that is not shared across the cluster, so consecutive logins
   * can disagree. `hasPendingClaimToken()` has
   * just cleared the marker as a side effect of being asked, and restarting with nothing
   * set is what dropped a newly-connected client on the Subscribe paywall.
   *
   * Bounded, because restarting forever is worse than the paywall — see
   * `coach-claim-reload-bridge.ts` for the rule and the limit.
   *
   * Returns whether the marker was put back. **Never throws**: the caller restarts the app
   * immediately after this, and a failed write must not cost them that restart — it would
   * leave them sitting on this page with nothing left to move them.
   */
  private rearmClaimBridgeIfAllowed(): boolean {
    let decision: ClaimBridgeReloadDecision = { shouldRearm: false, nextRearmCount: 0 };
    try {
      decision = decideClaimBridgeReload(
        parseClaimBridgeRearmCount(sessionStorage.getItem(COACH_CLAIM_BRIDGE_REARMS_KEY)),
      );
      if (decision.shouldRearm) {
        // count FIRST, marker second, and the order is load-bearing. If the marker were
        // written first and the count write then threw, the bridge would be armed with its
        // count never advancing — every restart would re-arm, which is the endless restart
        // loop the limit exists to prevent. This way a half-done write costs one allowance
        // and the bound still holds.
        sessionStorage.setItem(COACH_CLAIM_BRIDGE_REARMS_KEY, String(decision.nextRearmCount));
        sessionStorage.setItem(COACH_CLAIM_PENDING_RELOAD_KEY, '1');
        return true;
      }
    } catch (err) {
      // session storage refused (quota, or storage disabled in an embedded webview). The
      // restart still has to happen, so report and carry on unarmed.
      this.logging.trackSilentProblem('CoachClaim: could not re-arm the reload bridge', {
        error: describeError(err),
      });
      return false;
    }
    // out of re-arms. Report it: reaching this means the roles never settled across three
    // start-ups, which is the server-side staleness in #4533 lasting far longer than the
    // seconds it normally does, and the person is about to be routed by whatever the last
    // login said — possibly onto the paywall.
    //
    // This writes a console.error, and it IS on the E2E browser-error ignore list in
    // coldbrew.spec.ts, scoped to this exact report name. That reverses what #4533 decided,
    // on evidence #4533 did not have: it assumed reaching this line means the person is
    // being dropped on the paywall, so a run reaching it should go red. That is not what
    // happens. Running out of re-arms only means the waiting stopped — the person is then
    // routed by whatever the last login said, which is often "paid". On the second attempt
    // of CI run 30700989009 this line ran AND the client reached Home: 2343 checks passed
    // and the only failure was this report.
    //
    // What still fails a run is the person actually landing on the paywall, because the
    // coach scenario waits for the Home page (coach-client-lifecycle.spec.ts). So the
    // failure worth catching is still caught; what stopped happening is this report failing
    // a run on its own. Sentry is where the condition itself is watched.
    this.logging.trackSilentProblem('CoachClaim: role never settled across reloads', {
      rearms: decision.nextRearmCount,
    });
    return false;
  }

  /**
   * Poll couch login until the Paid role propagates, then reload to unstick
   * CoachClaimPage. Called after reload when the sessionStorage bridge flag is
   * still set because the pre-reload retry loop (in processClaimAsync) gave up
   * before the role landed. If 60s elapses without the role appearing, clear
   * the flag and show an error alert so the user isn't trapped on an empty page.
   *
   * The loop runs on the service (singleton) rather than the page because its
   * only exit is a full app reload — CoachClaimPage has no nav-away path in
   * normal operation, so a destroy-guard would be dead weight here.
   */
  async waitForRoleAndUnstickAsync(): Promise<void> {
    // if ionViewDidEnter fires twice (e.g. Ionic re-enter after an overlay), a
    // second loop would race the first to call reloadAppAsync / showAlertAsync —
    // the guard also keeps the sessionStorage clearing single-owner
    if (this.unstickInProgress) return;
    if (!this.hasPendingClaimToken()) return;
    this.unstickInProgress = true;

    try {
      // nominal schedule: login → check → 3s sleep, repeat. attemptCouchLoginAsync
      // has its own [2s, 5s, 10s] retries on network failure (auth.service.ts), so
      // a single failing iteration can eat ~17s of the budget under true outage —
      // the hard 60s cap still kicks in to show the timeout alert in that case.
      const pollIntervalMs = 3000;
      const maxDurationMs = 60000;
      const startMs = Date.now();
      console.log('[CLAIM] waitForRoleAndUnstick: polling for post-reload role propagation');

      while (Date.now() - startMs < maxDurationMs) {
        // defensive try: attemptCouchLoginAsync today swallows its own errors and
        // returns false, but a future change shouldn't leave the user trapped
        // with unstickInProgress=false and the sessionStorage flag still set
        try {
          await this.auth.attemptCouchLoginAsync(CouchLoginReason.CoachClaimUnstickPoll);
        } catch (err) {
          console.log('[CLAIM] waitForRoleAndUnstick: attemptCouchLoginAsync threw, continuing poll', err);
        }
        // if the flag auto-cleared (role >= Paid), reload — back to this page if the
        // bridge was re-armed, onward to home if it was not
        if (!this.hasPendingClaimToken()) {
          const rearmed = this.rearmClaimBridgeIfAllowed();
          console.log(`[CLAIM] waitForRoleAndUnstick: role propagated, reloading (${rearmed ? 'bridge re-armed — back to this page if the next login reads stale' : 'bridge not re-armed — routing by role'})`);
          await getMyApp().reloadAppAsync({ resetToRoot: true });
          return;
        }
        await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
      }

      // transient failure: the user-visible alert drives recovery (they can tap OK and try again)
      console.warn('[CLAIM] waitForRoleAndUnstick: timed out after 60s — clearing flag and surfacing activationTimeout alert');
      sessionStorage.removeItem(COACH_CLAIM_PENDING_RELOAD_KEY);
      // the bridge is being abandoned, so its re-arm count has nothing left to bound (#4533).
      // Left behind, it would eat the allowance of a second claim made in this same tab.
      sessionStorage.removeItem(COACH_CLAIM_BRIDGE_REARMS_KEY);
      await this.alertCtrl.showAlertAsync({
        title: LL.coach.claim.title(),
        message: LL.coach.claim.activationTimeout(),
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: true }],
      });
      await getMyApp().reloadAppAsync({ resetToRoot: true });
    } finally {
      this.unstickInProgress = false;
    }
  }

  /**
   * Wait for Firebase to finish settling its sign-in state before we call the server.
   *
   * #4466: the claim runs from CoachClaimPage's `ionViewDidEnter`, which on a WARM RESUME (the app
   * coming back from the background and restoring itself from cache) fires while Firebase's auth
   * listener has not yet run. AuthService deliberately does not wait for it there, because that
   * wait can hang for around ten seconds on Android (#3184) — so `fbAuth.currentUser` is still null,
   * the claim goes out with no auth token, and it throws. That is the one real-world event on
   * Sentry issue 7469456257, and its breadcrumbs show exactly that sequence ("Db:user: Cannot
   * initialize - no auth", then "Warm resume Phase B complete", then the failure).
   *
   * `whenFirebaseAuthSettled` is the primitive built for precisely this; the food search and the
   * SSO service already wait on it the same way. The wait is CAPPED so a permanently stuck Firebase
   * cannot hold the claim open forever — on timeout we go ahead and call anyway, which is no worse
   * than the old behaviour, and the failure path now says something true either way. The catch is
   * narrowed to TimeoutError so a future change that rejects the promise is not mistaken for one.
   */
  private async waitForFirebaseAuthSettledAsync(): Promise<void> {
    try {
      await timeout(FIREBASE_AUTH_SETTLE_WAIT_MS, this.auth.whenFirebaseAuthSettled);
    } catch (err) {
      if (err?.name !== 'TimeoutError') throw err;
      console.warn(`[CLAIM] processClaimAsync: Firebase auth did not settle within ${FIREBASE_AUTH_SETTLE_WAIT_MS}ms — claiming anyway`);
      // report it: proceeding anyway is a fallback, and without this we would never learn how often
      // Firebase fails to settle in time. The claim that follows often succeeds, in which case
      // nothing else on this path says a word about it.
      this.logging.trackSilentProblem('CoachClaim: Firebase auth did not settle before claim', {
        waitMs: FIREBASE_AUTH_SETTLE_WAIT_MS,
      });
    }
  }

  /**
   * Wait, briefly, for the documents this claim writes into to be loaded. Returns whether
   * they are (#4801).
   *
   * **Why this waits at all.** Start-up loads those documents one after another, and the
   * claim runs from `CoachClaimPage`'s `ionViewDidEnter`, which can fire while that is
   * still going. So a short wait covers the ordinary race.
   *
   * **Why it does NOT proceed on timeout, unlike `waitForFirebaseAuthSettledAsync` above.**
   * That one goes ahead anyway, and that is right for it: calling the server without a
   * settled sign-in is no worse than the old behaviour, and the call either works or fails
   * loudly. Here, going ahead is precisely what loses the data — the copy into an unloaded
   * document writes nothing and says nothing. Refusing costs the person one more tap on
   * their invitation link; proceeding cost one client their entire program.
   *
   * Polling rather than awaiting a promise because there is no "start-up finished" signal
   * to await, and start-up can be replaced part-way by a second one — so the only reliable
   * question is the one asked afresh each time: are the documents there NOW?
   */
  private async waitForUserDatabaseReadyAsync(): Promise<boolean> {
    const startMs = Date.now();
    while (true) {
      if (this.userDb.areCoachClaimDocumentsLoaded()) return true;
      if (Date.now() - startMs >= USER_DB_READY_WAIT_MS) return false;
      console.log('[CLAIM] processClaimAsync: user database not ready yet, waiting');
      await new Promise(resolve => setTimeout(resolve, USER_DB_READY_POLL_MS));
    }
  }

  /**
   * Ask the client before a coach's program replaces one they already have. Returns whether
   * to carry on with the claim — `false` means they said no.
   *
   * **This may only be called once the readiness gate has passed** (#4738). It reads the
   * client's training plan, and reading a document that start-up has not loaded yet is
   * exactly what this whole path got wrong: the read failed, the failure was absorbed, and
   * the question was skipped — not because the client had no program to lose, but because
   * we could not look. The claim then replaced their program without asking.
   *
   * **A failed read is therefore allowed to travel up to the caller, and that is the point
   * of this method rather than an oversight.** Once readiness is proven first, a throw here
   * is genuinely abnormal, and the only alternative to stopping is overwriting somebody's
   * training program with the consent question unanswered. Stopping costs them one more tap
   * on their invitation link — the token is still unspent, because the server has not been
   * called yet — and the outer catch in `processClaimAsync` tells them exactly that. The
   * read goes through `withDocWatcherAsync`, so the watcher is released either way.
   *
   * The loading spinner is dismissed before the question and put back if they accept: an
   * alert underneath the loading overlay cannot be read, let alone answered.
   */
  private async confirmOverwriteIfNeededAsync(loading: Loading, slotId: string): Promise<boolean> {
    const plan: FitnessPlan = await this.userDb.database.withDocWatcherAsync(watcher => this.userDb.loadPlanAsync(watcher));

    // if there is no program to lose, there is nothing to ask about. `plan` itself is read
    // straight, without a `?.`, on purpose: `loadPlanAsync` either hands back a document or
    // throws, so a missing one here would mean that contract had broken — and skipping the
    // question on it is the very thing this method exists to stop. A brand-new account is
    // not that case: it has a real plan document whose `ready` is simply not set yet.
    if (!plan.ready) return true;

    console.log(`[CLAIM] processClaimAsync: plan is ready, showing overwrite warning, slot=${slotId}`);
    await loading.dismiss();
    const confirmed = await this.alertCtrl.showAlertAsync<boolean>({
      title: LL.coach.claim.title(),
      message: LL.coach.claim.overwriteWarning(),
      buttons: [
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: false },
        { text: LL.coach.claim.accept(), role: AlertButtonRole.Primary, result: true },
      ],
    });
    // only worth putting the spinner back if the claim is going to continue
    if (confirmed) await loading.present();
    return !!confirmed;
  }

  async processClaimAsync(opts?: CoachClaimProcessOptions): Promise<void> {
    const token = this.getClaimTokenFromUrl();
    if (!token || this.claimInProgress) return;
    this.claimInProgress = true;
    const slotId = this.getSlotId(token);
    console.log(`[CLAIM] processClaimAsync: slot=${slotId}, checkOverwrite=${!!opts?.checkOverwrite}`);

    const loading = this.loadingCtrl.create({ content: LL.coach.claim.settingUp() });
    // whether the server has already accepted the claim by the time something throws. The try below
    // covers far more than the claim call itself — applying the copied program and pushing it to the
    // server are both inside it — and what we may honestly say afterwards depends on which side of
    // the claim we failed on.
    let claimAccepted = false;
    try {
      await loading.present();
      await this.waitForFirebaseAuthSettledAsync();

      // #4801: refuse to claim into a database whose documents are not loaded, and do it
      // HERE — before the server call — because that is what keeps the token unspent.
      //
      // #4738: and before the overwrite question below, which is the order that matters.
      // This gate used to sit UNDER that question, so on an unloaded database the question
      // read a plan that was not there, absorbed the failure, and stayed silent — and if the
      // documents then finished loading inside this very wait, the claim carried on and
      // replaced the client's program without anyone being asked. Everything that reads one
      // of the client's documents now happens after this line. That also covers the shape
      // where there is no database at all: `areCoachClaimDocumentsLoaded` answers that
      // safely, so it ends here in a stated refusal, where it used to throw out of
      // `ionViewDidEnter` with no message at all and leave the page unable to try again.
      if (!await this.waitForUserDatabaseReadyAsync()) {
        console.error(`[CLAIM] processClaimAsync: user database not ready — refusing to claim, slot=${slotId}`);
        this.logging.trackSilentProblem('CoachClaim: refused, user database not ready', {
          slotId,
          waitMs: USER_DB_READY_WAIT_MS,
        });
        await this.dismissAndShowFailureAsync(loading, CoachClaimFailureKind.DatabaseNotReady);
        return;
      }

      // if checking for overwrite, ask before a coach's program replaces an active one.
      // the cancel branch lives here rather than inside the helper because declining is not
      // a failure — nothing is reported, we simply clear the token and start the app again.
      if (opts?.checkOverwrite && !await this.confirmOverwriteIfNeededAsync(loading, slotId)) {
        console.log(`[CLAIM] processClaimAsync: user cancelled overwrite`);
        this.clearClaimTokenFromUrl();
        await getMyApp().reloadAppAsync({ resetToRoot: true });
        return;
      }

      console.log(`[CLAIM] processClaimAsync: calling coachClaimInviteAsync, slot=${slotId}`);
      // one id for this claim (#6891). The automatic retries resend these same params, so a retry
      // whose first answer was lost on the network is recognised by the server as this same claim
      // and answered as a success. Reopening the link runs this method again with a new id.
      const claimAttemptId = crypto.randomUUID();
      const result = await this.cloudFunctions.coachClaimInviteAsync({ claimToken: token, claimAttemptId });

      // expected user-visible rejection — see design-docs/coach-mode.md
      // §"Expected vs unexpected rejections" for the contract. no
      // trackSilentProblem here; the outer catch keeps that for unexpected
      // throws.
      if (!result.success) {
        console.log(`[CLAIM] processClaimAsync: server rejected token, slot=${slotId}`);
        // the server looked at the token and said no — the only case that may blame the link
        await this.dismissAndShowFailureAsync(loading, CoachClaimFailureKind.TokenRejected);
        return;
      }

      // from here on the server has marked the slot claimed, so the token is SPENT. Anything that
      // throws below must not tell the person their link still works (#4466) — reopening it would
      // only earn them the "invalid or already used" refusal.
      claimAccepted = true;

      console.log(`[CLAIM] processClaimAsync: claim succeeded, coachUid=${result.coachUid}`);

      // clear the token immediately after claim succeeds — if applyClaimData or
      // attemptCouchLogin throws, we must not retry the already-consumed token
      this.clearClaimTokenFromUrl();

      // log the result shape so we can see what the server returned even when
      // the apply ends up no-op'ing or throwing on a particular stage (#3315)
      console.log(`[CLAIM] processClaimAsync: result shape — intakeFormState=${!!result.intakeFormState}, fitnessPlan=${!!result.fitnessPlan} (welcomePhase=${result.fitnessPlan?.welcomePhase}, ready=${result.fitnessPlan?.ready}, workouts=${result.fitnessPlan?.workouts?.length}, subSlots=${Object.keys(result.fitnessPlan?.subSlots || {}).length}), fitnessState=${!!result.fitnessState} (weight_kg=${result.fitnessState?.weight_kg})`);

      await this.applyClaimDataAsync(result, slotId);
      await this.auth.attemptCouchLoginAsync(CouchLoginReason.CoachClaimAfterApply);
      // retry couch login until the Paid role propagates — the server just granted it
      // (see coachClaimInvite.ts couchPutUserInfo), but CouchDB may lag by a few hundred ms.
      // reloading before the role lands leaves the post-reload couchLogin returning
      // empty roles and strands the user on CoachClaimPage (see d8297d4ae).
      // budget is short (~3.7s total) to keep the "Setting up your program…" spinner
      // snappy; post-reload waitForRoleAndUnstickAsync handles slower propagation.
      const preReloadRetryDelaysMs = [200, 500, 1000, 2000];
      for (const delay of preReloadRetryDelaysMs) {
        if (this.auth.getAuthRole() >= AuthRole.Paid) break;
        console.log(`[CLAIM] processClaimAsync: role=${this.auth.getAuthRole()} not yet Paid, retrying couchLogin in ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
        await this.auth.attemptCouchLoginAsync(CouchLoginReason.CoachClaimRolePropagation);
      }
      if (this.auth.getAuthRole() < AuthRole.Paid) {
        console.warn(`[CLAIM] processClaimAsync: role still not propagated pre-reload — CoachClaimPage will poll post-reload`);
      }
      // force-cache the fresh role to localStorage before the upcoming reload —
      // if couchLogin is slow/fails on the second boot, the cached role is the fallback
      this.auth.getAuthRole();

      // option A: ensure welcomePhase=Done so the app boots to the home page
      // (not the intake form). option B (#3389): applyClaimDataAsync already
      // set welcomePhase=CoachedIntake so the client lands on the coached
      // intake wizard; do NOT overwrite to Done here.
      const plan: FitnessPlan = await this.userDb.database.withDocWatcherAsync(watcher => this.userDb.loadPlanAsync(watcher));
      if (!result.clientFills && plan.welcomePhase !== WelcomePhase.Done) {
        console.log(`[CLAIM] processClaimAsync: fixing welcomePhase from '${plan.welcomePhase}' to 'done'`);
        plan.welcomePhase = WelcomePhase.Done;
        plan.ready = true;
        await this.userDb.savePlanAsync(DatabaseWrapper.getTransactionId());
      }

      // push all local changes to remote CouchDB before showing the success alert —
      // if the user closes the app after the alert, the data must already be on the server
      // (also needed for coach's "Manage Client" which loads from remote)
      //
      // #4801: and we now CHECK that it happened. This push can decline to run — most
      // importantly when the wrapper has no remote database, which is what a failed boot
      // leaves behind. It used to say nothing, so the claim carried straight on to the
      // "Connected to your coach!" alert while the coach's program existed only on this
      // device, where the next sign-out wiped it. Throwing here hands the outer catch a
      // failure with `claimAccepted` already true, so the person is told the one true
      // thing: they are connected to their coach, but the program copy did not finish.
      const pushOutcome = await this.userDb.database.pushToRemoteAsync();
      if (!didPushReachServer(pushOutcome)) {
        throw new Error(`coach claim data did not reach the server (${pushOutcome})`);
      }

      await loading.dismiss();
      await this.alertCtrl.showAlertAsync({
        title: LL.coach.claim.title(),
        message: LL.coach.claim.connected({ coachName: result.coachName }),
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: true }],
      });
      // flag survives the reload so hasPendingClaimToken() keeps the user off
      // the Subscribe page until CouchDB roles propagate
      sessionStorage.setItem(COACH_CLAIM_PENDING_RELOAD_KEY, '1');
      // a fresh claim starts with its full allowance of bridge re-arms (#4533). Set rather than
      // cleared, so a count left over from an earlier claim in this same tab cannot shorten it.
      sessionStorage.setItem(COACH_CLAIM_BRIDGE_REARMS_KEY, '0');
      await getMyApp().reloadAppAsync({ resetToRoot: true });
    } catch (err) {
      // trackSilentProblem so the throw lands in Sentry — the previous bare
      // console.error left no trace and prod failures (#3315) were invisible.
      // keep a console.error alongside so local devs still see the rich `err`
      // object in DevTools (trackSilentProblem only stringifies message+stack).
      console.error(`[CLAIM] processClaimAsync: claim FAILED, slot=${slotId}`, err);
      // #4466: name WHICH kind of failure this was. Sentry issue 7469456257 had two quite different
      // causes filed under one title — sign-in not having finished, and an HTTP 500 — and there was
      // no way to tell them apart in the report.
      const kind = classifyCoachClaimFailure({
        serverAcceptedClaim: claimAccepted,
        // a rejected token does not throw — the server answers success:false, handled above — so
        // anything reaching this catch before the claim was accepted got no verdict on the token
        serverRejectedToken: false,
        isAuthNotReady: err instanceof CloudFunctionAuthError,
        // isNetworkError covers the connection failing; it deliberately does NOT match an HTTP
        // status, and 5xx is only retried for 502/503 — so a plain 500, which is most of the
        // recorded events, needs naming here or it would fall through as unexplained
        isServerUnreachableOrFailed: isNetworkError(err)
          || (err instanceof CloudFunctionHttpError && err.httpStatus >= 500),
      });
      // put the kind in the REPORT NAME, not only in the context. Context becomes a Sentry
      // breadcrumb, which is not indexed — so the causes would still have grouped into one issue
      // nobody could split, which is the thing naming them was supposed to fix.
      this.logging.trackSilentProblem(`CoachClaim: processClaimAsync FAILED (${kind})`, { ...this.buildErrContext(slotId, err), kind });
      await this.dismissAndShowFailureAsync(loading, kind);
    }
  }

  private async applyClaimDataAsync(
    result: Awaited<ReturnType<CloudFunctionsService['coachClaimInviteAsync']>>,
    slotId: string,
  ) {
    const transaction = DatabaseWrapper.getTransactionId();

    // run each stage through this so a throw lands in Sentry with the stage
    // name attached, and the throw still propagates to the outer catch in
    // processClaimAsync (#3315 diagnostic).
    const runStage = async (stage: string, body: () => Promise<void>) => {
      console.log(`[CLAIM] applyClaimDataAsync: stage=${stage} starting`);
      try {
        await body();
        console.log(`[CLAIM] applyClaimDataAsync: stage=${stage} ok`);
      } catch (err) {
        // console.error in addition to trackSilentProblem so local devs see
        // the rich err object in DevTools alongside the Sentry breadcrumb.
        console.error(`[CLAIM] applyClaimDataAsync: stage=${stage} threw, slot=${slotId}`, err);
        this.logging.trackSilentProblem(`CoachClaim: stage '${stage}' threw`, this.buildErrContext(slotId, err));
        // re-throw so the outer catch in processClaimAsync still fires
        // (alert + reload), preserving the original error-handling contract
        throw err;
      }
    };

    await this.userDb.database.withDocWatcherAsync(async watcher => {
      // save intake form state
      if (result.intakeFormState) {
        await runStage('intakeForm', async () => {
          const intakeForm = await this.userDb.loadIntakeFromStateAsync(watcher);
          deepOverwriteCouchSafe(intakeForm, result.intakeFormState);
          await this.userDb.saveIntakeFromStateAsync(transaction);
        });
      }

      // save fitness plan
      if (result.fitnessPlan) {
        await runStage('plan', async () => {
          const plan = await this.userDb.loadPlanAsync(watcher);
          deepOverwriteCouchSafe(plan, result.fitnessPlan);
          await this.userDb.savePlanAsync(transaction);
        });
      }

      // save fitness state
      if (result.fitnessState) {
        await runStage('fitnessState', async () => {
          const fitnessState = await this.userDb.loadFitnessStateAsync(watcher);
          deepOverwriteCouchSafe(fitnessState, result.fitnessState);
          await this.userDb.saveFitnessStateAsync(transaction);
        });
      }

      // derive and save declared + immutable state from intake form
      if (result.intakeFormState) {
        await runStage('declared+immutable', async () => {
          const declaredState = await this.userDb.loadDeclaredStateAsync(watcher);
          // claim finalization builds a fresh plan for the client, so seed
          // new-user defaults (rest-timer ON, 2:00) alongside the mapped fields
          deepOverwriteCouchSafe(declaredState, mapIntakeFormToDeclaredState(result.intakeFormState, { seedNewUserDefaults: true }));
          await this.userDb.saveDeclaredStateAsync(transaction);

          const immutableState = await this.userDb.loadImmutableStateAsync(watcher);
          deepOverwriteCouchSafe(immutableState, mapIntakeFormToImmutableState(result.intakeFormState));
          await this.userDb.saveImmutableStateAsync(transaction);
        });
      }

      // generate schedule rules from the declared state (mirrors intake form finalization)
      // without this, the client has a plan but no schedule — so no daily log entries appear
      if (result.intakeFormState) {
        await runStage('schedule', async () => {
          const declaredState = await this.userDb.loadDeclaredStateAsync(watcher);
          const defaultSchedule = await this.svPlanHandler.generateDefaultScheduleAsync(declaredState);
          const schedule = await this.userDb.loadScheduleAsync();
          deepOverwriteCouchSafe(schedule, defaultSchedule);
          await this.userDb.saveScheduleAsync(transaction);
        });
      }

      // option B (#3389): no intake / plan / state was transferred — the
      // client will fill the intake themselves on their own account. Two
      // writes belong together for an option-B claim: set the plan's
      // welcomePhase to CoachedIntake so app.component routes the client
      // into the coached intake wizard (PRD Step 8), and write the
      // clientSlotId into coach-relationship/as-client so later steps can
      // address the coach's slot (intakeSubmitted notification, slot
      // lifecycle flips). Bundling them inside one stage keeps the two
      // option-B-only writes inside the same failure boundary — if either
      // throws, runStage rethrows so the outer catch in processClaimAsync
      // shows an alert and reloads. That alert says the client is connected
      // to their coach but the program copy did not finish (#4466): by this
      // point the server has accepted the claim, so it must NOT be the
      // invalid-token wording, nor the one telling them to reopen the link.
      // A partial success is
      // still possible (CouchDB has no cross-doc transactions), but the
      // surrounding option-A flow has the same limitation and the user-
      // visible UX (alert + reload) is consistent.
      if (result.clientFills) {
        await runStage('coachedIntake', async () => {
          const plan = await this.userDb.loadPlanAsync(watcher);
          plan.welcomePhase = WelcomePhase.CoachedIntake;
          await this.userDb.savePlanAsync(transaction);

          if (result.clientSlotId) {
            const relDoc = this.userDb.loadCoachRelAsClient();
            relDoc.clientSlotId = result.clientSlotId;
            await this.userDb.saveCoachRelAsClientAsync(transaction);
          }
        });
      }

      // update coach-relationship/as-client singleton (coach link, name,
      // and re-claim formerCoaches cleanup). For option B (#3389) the
      // coachedIntake stage above already wrote clientSlotId into the
      // same doc, but stage barriers are at the granularity of these
      // runStage blocks — re-loading the doc here picks up the prior
      // write via the watcher.
      await runStage('coachRelAsClient', async () => {
        const relDoc = this.userDb.loadCoachRelAsClient();
        relDoc.coachUid = result.coachUid;
        relDoc.coachName = result.coachName;
        // Tier 1 white-labeling (#3707): persist the coach branding returned by
        // the claim so the app themes from the as-client doc on the next boot.
        // Absent branding leaves the field undefined (clean MH fallback).
        relDoc.branding = result.branding;
        // Phase 3: if the user previously had this same coach (re-claim flow),
        // remove the matching `formerCoaches` entry. See
        // plans/coach-exercise-sharing-phase3.md §5.
        relDoc.formerCoaches = removeFormerCoachByUid(relDoc.formerCoaches, result.coachUid);
        // #4817: record that the handover actually finished, and record it HERE
        // for two reasons that together are the whole of its meaning.
        //
        // This is the LAST stage, so reaching this line means every earlier
        // stage — the intake, the plan, the fitness state, the derived states,
        // the schedule — already succeeded. And it rides in this stage's own
        // save, so it only reaches the server in the push that processClaimAsync
        // checks with didPushReachServer. A claim that fell over anywhere, or
        // whose push never arrived, therefore leaves this absent.
        //
        // Absence is what the coach's dashboard reads. In #4738 the server
        // accepted the claim, the copy into the client's account failed, and the
        // coach spent a week reviewing an active-looking client against an
        // intake they had never received, because nothing recorded the failure.
        relDoc.handoverCompletedAt = DateTime.utc().toISO();
        await this.userDb.saveCoachRelAsClientAsync(transaction);
      });
    });
  }
}
